import { Chart } from 'chart.js'
import dayjs from 'dayjs'
import { logoEuro } from 'ionicons/icons'
import React, { useContext, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'

import { AppContext } from '../../../contexts/AppContext'
import useUser from '../../../contexts/hooks/useUser'
import usePreference from '../../../utils/preferences/usePreference'
import MarkedPeriodsToggle from '../../electricity/charts/priceChart/MarkedPeriodsToggle'
import { useMarkedPeriods } from '../../electricity/charts/priceChart/useMarkedPeriods'
import createHourpriceChart from './HourpriceChart'
import OverViewCard from './OverviewCard'
import { priceString } from '../../../utils/priceUtils'
import { useMeanAggregatedDayAheadPrices } from '../../../hooks/dayAheadPrices/useMeanAggregatedDayAheadPrices'

dayjs.extend(isSameOrBefore)

const PricesCard: React.FC = () => {
	const now = dayjs()

	const chartElement = useRef<HTMLCanvasElement>(null)
	const { isDarkMode } = useContext(AppContext)

	const { t } = useTranslation('pages_overview')

	const getAnnotations = useMarkedPeriods('hour')

	const [frontPagePriceSegments, setFrontPagePriceSegments] = usePreference(
		'frontPagePriceSegments',
		'disabled'
	)

	const { userSettings } = useUser()
	const { dayAheadPrices } = useMeanAggregatedDayAheadPrices({
		from: now.startOf('day'),
		to: now.endOf('day'),
		targetResolution: 'hour',
	})

	const showMarkedPeriods = frontPagePriceSegments === 'enabled'

	const handleMarkedPeriodsToggle = async (newValue: 'enabled' | 'disabled') => {
		setFrontPagePriceSegments(newValue)
	}

	useEffect(() => {
		if (!dayAheadPrices || !chartElement.current) return

		const existingChart = Chart.getChart(chartElement.current)
		if (existingChart) {
			existingChart.destroy()
		}

		const upcomingPrices = dayAheadPrices.filter(p => p.deliveryStart.hour() >= now.hour() - 1)

		const annotations = getAnnotations(
			{ values: [{ data: upcomingPrices.map(p => p.centsPerKwh) }] },
			true,
			isDarkMode
		)

		const newChart = createHourpriceChart(
			chartElement.current,
			upcomingPrices,
			isDarkMode,
			annotations,
			showMarkedPeriods
		)
		return () => {
			newChart.destroy()
		}
	}, [userSettings?.themeMode, isDarkMode, showMarkedPeriods, dayAheadPrices, getAnnotations, now])

	const priceNow = dayAheadPrices.find(
		price => price.deliveryStart.isSameOrBefore(now) && price.deliveryEnd.isAfter(now)
	)

	return (
		<OverViewCard
			headerText={t('pricesCard.otsikko')}
			icon={logoEuro}
			linkTarget='/exchange/price/day'
			className='stockprices'
			headerSubtext={
				<div className='subtext'>
					{priceNow && <>
						<div className='now bg-brand-secondary-100-light dark:bg-brand-secondary-100-dark'>
							<span className='dark:text-text-dark'>{t('nyt', { ns: 'common' })}</span>
						</div>
						<div className='text-brand-secondary-200-dark dark:text-brand-primary-100-dark'>
							{priceString(priceNow.centsPerKwh)}
							<span className='unit text-text-grey-200 dark:text-text-grey-100'>snt/kWh</span>
						</div>
					</>}
				</div>
			}
		>
			<div className='stockprice-chart'>
				<canvas id='priceschart' height='50' ref={chartElement}></canvas>
			</div>
			<MarkedPeriodsToggle
				showMarkedPeriods={showMarkedPeriods}
				setShowMarkedPeriods={handleMarkedPeriodsToggle}
			/>
		</OverViewCard>
	)
}

export default PricesCard
